import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft, Calendar, Clock, MapPin, Mail, Phone,
  DollarSign, FileText, Copy, Check, ExternalLink,
  MessageCircle, ThumbsUp, ThumbsDown, Link
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

// ── Normalizers for mixed Booking / booking_request shapes ─────────────────
const getName    = (b) => b?.customer_name  || b?.customer?.name  || 'N/A';
const getEmail   = (b) => b?.customer_email || b?.customer?.email || '';
const getPhone   = (b) => b?.customer_phone || b?.customer?.phone || '';
const getService = (b) => b?.listing_title  || b?.service?.name   || 'N/A';
const getDate    = (b) => b?.event_date     || b?.schedule?.date  || null;
const getTime    = (b) => b?.schedule?.time || null;
const getAmount  = (b) => b?.amount         || b?.pricing?.total  || 0;
const getNotes   = (b) => b?.notes          || b?.customer_notes  || '';
// ───────────────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  pending:   'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-green-100 text-green-800',
  completed: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-red-100 text-red-800',
  rejected:  'bg-red-100 text-red-800',
};

function CopyButton({ text, label = 'Copy', 'data-testid': testId }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleCopy}
      className="gap-1.5"
      data-testid={testId}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied!' : label}
    </Button>
  );
}

const BookingDetailsNew = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { apiCall, user } = useAuth();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminNotes, setAdminNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [approveDialog, setApproveDialog] = useState(false);
  const [approvingLoading, setApprovingLoading] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingLoading, setRejectingLoading] = useState(false);

  const fetchBooking = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiCall(`/api/bookings/${id}`);
      setBooking(data);
      setAdminNotes(data.admin_notes || '');
    } catch {
      toast({ title: 'Error', description: 'Failed to load booking details', variant: 'destructive' });
      navigate('/bookings');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchBooking(); }, [fetchBooking]);

  const handleApprove = async () => {
    try {
      setApprovingLoading(true);
      const res = await apiCall(`/api/bookings/${id}/approve`, { method: 'PUT' });
      toast({ title: 'Approved!', description: res.payment_link ? 'Payment link sent to customer.' : 'Booking approved.' });
      setApproveDialog(false);
      fetchBooking();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setApprovingLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setRejectingLoading(true);
      await apiCall(`/api/bookings/${id}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason }),
      });
      toast({ title: 'Rejected', description: 'Customer has been notified.' });
      setRejectDialog(false);
      fetchBooking();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setRejectingLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      setCancelling(true);
      await apiCall(`/api/bookings/${id}/cancel`, { method: 'PUT' });
      toast({ title: 'Cancelled', description: 'Booking cancelled successfully.' });
      setCancelDialog(false);
      fetchBooking();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setCancelling(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setSavingNotes(true);
      await apiCall(`/api/bookings/${id}/notes?admin_notes=${encodeURIComponent(adminNotes)}`, { method: 'PUT' });
      toast({ title: 'Saved', description: 'Notes saved.' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSavingNotes(false);
    }
  };

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Loading booking details...</p>
      </div>
    </DashboardLayout>
  );

  if (!booking) return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Booking not found</p>
      </div>
    </DashboardLayout>
  );

  const status       = booking.status || 'pending';
  const paymentLink  = booking.payment_link;
  const whatsappUrl  = booking.whatsapp_url;
  const isPending    = status === 'pending';
  const isConfirmed  = status === 'confirmed';

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl">

        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/bookings')} data-testid="back-to-bookings">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Bookings
          </Button>
        </div>

        {/* Title card */}
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-wrap justify-between items-start gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-3 mb-1">
                  <h1 className="text-2xl font-bold" data-testid="booking-number">
                    {booking.booking_number || id.substring(0, 8)}
                  </h1>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-800'}`} data-testid="booking-status">
                    {status}
                  </span>
                  {booking.booking_type && (
                    <Badge variant="outline" className="capitalize">{booking.booking_type}</Badge>
                  )}
                </div>
                <h2 className="text-lg text-gray-600" data-testid="booking-service-name">{getService(booking)}</h2>
                <div className="space-y-1 text-sm text-gray-500 mt-2">
                  {getDate(booking) && (
                    <p className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {getDate(booking)}{getTime(booking) ? ` at ${getTime(booking)}` : ''}
                    </p>
                  )}
                  {(booking.guests > 1) && (
                    <p className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Guests: {booking.guests}
                    </p>
                  )}
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-2">
                {isPending && (
                  <>
                    <Button
                      className="bg-green-600 hover:bg-green-700 text-white gap-1.5"
                      onClick={() => setApproveDialog(true)}
                      data-testid="approve-btn"
                    >
                      <ThumbsUp className="h-4 w-4" /> Approve
                    </Button>
                    <Button
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50 gap-1.5"
                      onClick={() => setRejectDialog(true)}
                      data-testid="reject-btn"
                    >
                      <ThumbsDown className="h-4 w-4" /> Reject
                    </Button>
                  </>
                )}
                {isConfirmed && (
                  <Button variant="outline" className="text-red-600" onClick={() => setCancelDialog(true)}>
                    Cancel Booking
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Link & WhatsApp card — shown when payment_link exists */}
        {paymentLink && (
          <Card className="border-green-200 bg-green-50" data-testid="payment-link-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                  <Link className="h-4 w-4 text-green-700" />
                </div>
                <div>
                  <p className="font-semibold text-green-900">Payment Link Generated</p>
                  <p className="text-xs text-green-700">Share this with the customer to collect payment</p>
                </div>
              </div>

              {/* Payment link row */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <a
                  href={paymentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline break-all font-mono"
                  data-testid="payment-link-url"
                >
                  {paymentLink}
                </a>
                <CopyButton text={paymentLink} label="Copy Link" data-testid="copy-payment-link-btn" />
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => window.open(paymentLink, '_blank')}
                  data-testid="open-payment-link-btn"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Open
                </Button>
              </div>

              {/* WhatsApp share row */}
              {whatsappUrl && (
                <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-green-200">
                  <p className="text-sm text-green-800 font-medium">Share via WhatsApp:</p>
                  <Button
                    size="sm"
                    className="bg-[#25D366] hover:bg-[#1ebe5d] text-white gap-1.5"
                    onClick={() => window.open(whatsappUrl, '_blank')}
                    data-testid="whatsapp-share-btn"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Send on WhatsApp
                  </Button>
                  <CopyButton text={whatsappUrl} label="Copy WhatsApp Link" data-testid="copy-whatsapp-link-btn" />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Summary stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center">
                  <Calendar className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Booked On</p>
                  <p className="font-semibold text-sm">
                    {booking.created_at ? new Date(booking.created_at).toLocaleDateString('en-IN') : '—'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-green-100 flex items-center justify-center">
                  <DollarSign className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Amount</p>
                  <p className="font-semibold text-sm" data-testid="booking-amount">₹{Number(getAmount(booking)).toLocaleString('en-IN')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-yellow-100 flex items-center justify-center">
                  <FileText className="h-4 w-4 text-yellow-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Payment</p>
                  <p className="font-semibold text-sm capitalize">
                    {booking.paid_at ? 'Paid' : (paymentLink ? 'Link Sent' : 'Pending')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-purple-100 flex items-center justify-center">
                  <MapPin className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Type</p>
                  <p className="font-semibold text-sm capitalize">{booking.booking_type || 'Request'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Customer + Booking details in 2 columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Customer Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-gray-500">Name</p>
                <p className="font-medium" data-testid="customer-name">{getName(booking)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 flex items-center gap-1"><Mail className="h-3 w-3" /> Email</p>
                <p className="font-medium text-sm break-all">{getEmail(booking) || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 flex items-center gap-1"><Phone className="h-3 w-3" /> Phone</p>
                <p className="font-medium">{getPhone(booking) || '—'}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Booking Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-gray-500">Listing / Service</p>
                <p className="font-medium">{getService(booking)}</p>
              </div>
              {getDate(booking) && (
                <div>
                  <p className="text-xs text-gray-500">Date</p>
                  <p className="font-medium">{getDate(booking)}{getTime(booking) ? ` · ${getTime(booking)}` : ''}</p>
                </div>
              )}
              {booking.guests && (
                <div>
                  <p className="text-xs text-gray-500">Guests / Qty</p>
                  <p className="font-medium">{booking.guests}</p>
                </div>
              )}
              {getNotes(booking) && (
                <div>
                  <p className="text-xs text-gray-500">Customer Notes</p>
                  <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">{getNotes(booking)}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Payment details (if paid) */}
        {booking.paid_at && (
          <Card className="border-blue-200" data-testid="payment-complete-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-blue-700">Payment Completed</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-xs text-gray-500">Paid Amount</p>
                <p className="font-semibold">₹{Number(booking.paid_amount || getAmount(booking)).toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Paid On</p>
                <p className="font-medium">{new Date(booking.paid_at).toLocaleString('en-IN')}</p>
              </div>
              {booking.razorpay_payment_id && (
                <div>
                  <p className="text-xs text-gray-500">Razorpay ID</p>
                  <p className="font-mono text-xs text-gray-700">{booking.razorpay_payment_id}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Admin notes */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Internal Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              rows={3}
              placeholder="Add internal notes about this booking..."
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              data-testid="admin-notes-input"
            />
            <Button onClick={handleSaveNotes} disabled={savingNotes} size="sm" data-testid="save-notes-btn">
              {savingNotes ? 'Saving...' : 'Save Notes'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Approve Dialog */}
      <Dialog open={approveDialog} onOpenChange={setApproveDialog}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve Booking Request</DialogTitle>
            <DialogDescription>
              A Razorpay payment link will be generated and emailed to the customer.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-2 text-sm">
            <p><span className="font-medium">Customer:</span> {getName(booking)}</p>
            <p><span className="font-medium">Service:</span> {getService(booking)}</p>
            <p><span className="font-medium">Date:</span> {getDate(booking) || '—'}</p>
            <p><span className="font-medium">Amount:</span> ₹{Number(getAmount(booking)).toLocaleString('en-IN')}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialog(false)}>Cancel</Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={handleApprove}
              disabled={approvingLoading}
              data-testid="confirm-approve-btn"
            >
              {approvingLoading ? 'Approving...' : 'Approve & Send Payment Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject Booking Request</DialogTitle>
            <DialogDescription>The customer will be notified by email.</DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-3">
            <div className="text-sm space-y-1">
              <p><span className="font-medium">Customer:</span> {getName(booking)}</p>
              <p><span className="font-medium">Service:</span> {getService(booking)}</p>
            </div>
            <Textarea
              placeholder="Reason for rejection (optional, sent to customer)..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={rejectingLoading}
              data-testid="confirm-reject-btn"
            >
              {rejectingLoading ? 'Rejecting...' : 'Reject Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialog} onOpenChange={setCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Booking</DialogTitle>
            <DialogDescription>Are you sure? This cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="py-3 text-sm space-y-1">
            <p><span className="font-medium">Booking:</span> {booking.booking_number}</p>
            <p><span className="font-medium">Customer:</span> {getName(booking)}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog(false)}>Keep</Button>
            <Button variant="destructive" onClick={handleCancel} disabled={cancelling}>
              {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default BookingDetailsNew;
